import { supabase } from '../config/supabaseClient.js';
import { loader } from '../ui/loader.js';
import { el, clear, formatarNome, mascararCpf, fmtDataHora, aplicarMascaraCpf, cpfValido, formatarCpf, formatarTitulo, limpavel } from '../lib/dom.js';
import { buscarTodos, orBuscaNomeCpf, orBuscaNomeCpfLegado, orBuscaPessoa, orBuscaPessoaLegado } from '../lib/db.js';
import { buscaSelect } from '../ui/buscaSelect.js';
import { renderTabela } from '../ui/table.js';
import { abrirModal } from '../ui/modal.js';
import { toast } from '../lib/toast.js';
import { operadorAtual } from '../lib/operador.js';
import { ehGestaoAtivo } from '../lib/permissoes.js';
import { sugerirSetor } from '../domain/sugestoes.js';
import { listarMotivos, listarEmpresas, criarEmpresaPendente } from '../domain/visitaListas.js';
import { assinarTabela, pollPeriodico } from '../lib/realtime.js';

const MOTIVO_GUIA = [
  ['Atendimento ao Paciente/Acompanhante', 'Marcação, agendamento, acesso ao próprio laudo/prontuário, internação, regulação, TFD, acompanhar paciente.'],
  ['Reunião ou Atendimento Administrativo', 'Reunião com direção, gerências, setores, comissões, RH, financeiro, jurídico; entrevista/seleção; representante comercial.'],
  ['Entrega ou Retirada de Documento', 'Atestado, documentação, declaração, protocolo, nota fiscal, currículo, cópia de laudo/prontuário, assinatura.'],
  ['Ensino e Atividade Acadêmica', 'Aula, estágio, internato, residência, preceptoria, liga, monitoria, workshop.'],
  ['Pesquisa', 'Coleta de dados, CEP, amostras, ensaio clínico, questionário, TCLE.'],
  ['Serviço Técnico, Manutenção ou Insumos', 'Manutenção/instalação/calibração de equipamento, TI, engenharia clínica, entrega de materiais/medicamentos/gases, resíduos, obras.'],
  ['Visita Institucional, Fiscalização ou Auditoria', 'Autoridade, comitiva, órgão de controle, vigilância sanitária, visita técnica externa, imprensa.'],
  ['Ouvidoria', 'Reclamação, elogio, solicitação ou denúncia formal.'],
  ['Outros', 'Qualquer caso fora dos acima — escreva o motivo (vai para aprovação do gestor).'],
];
function abrirGuiaMotivo() {
  abrirModal({
    titulo: 'Motivos da visita — o que entra em cada um', classe: 'modal--larga',
    corpo: MOTIVO_GUIA.map(([cat, entra]) => el('div', { style: 'margin-bottom:10px' }, [
      el('div', { style: 'font-weight:600', text: cat }),
      el('div', { class: 'view__sub', style: 'margin:2px 0 0', text: entra }),
    ])),
    acoes: [{ rotulo: 'Fechar', classe: 'btn btn--primario' }],
  });
}

function inicioDoDiaISO() { const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString(); }

async function opcoesSetores() {
  try {
    const data = await buscarTodos(() => supabase.from('setor').select('id, nome').eq('status', 'ATIVO').order('nome'));
    return data.map((s) => ({ value: s.id, label: s.nome }));
  } catch { return []; }
}

// Servidores cadastrados (para "com quem vai falar"); permite tambem digitar livre.
async function buscarColaboradoresFala(termo) {
  const t = (termo || '').trim();
  if (t.length < 2) return [];
  const run = (filtro) => supabase.from('colaborador').select('id, nome, matricula').eq('status', 'ATIVO').or(filtro).order('nome').limit(8);
  try {
    let { data, error } = await run(orBuscaPessoa(t));
    if (error) ({ data, error } = await run(orBuscaPessoaLegado(t)));
    return error ? [] : (data || []);
  } catch { return []; }
}

// Visitantes que ja vieram antes (banco derivado das visitas), p/ reaproveitar por nome/CPF.
async function buscarVisitantesRecorrentes(termo) {
  const t = (termo || '').trim().replace(/[%']/g, '');
  if (t.length < 2) return [];
  const run = (filtro, sel) => supabase.from('visita_administrativa')
    .select(sel).or(filtro)
    .order('registrado_em', { ascending: false }).limit(50);
  const cols = 'nome, cpf, empresa_id, empresa_representa, registrado_em';
  let { data, error } = await run(orBuscaNomeCpf(t), cols);
  if (error) ({ data, error } = await run(orBuscaNomeCpfLegado(t), cols));
  if (error) ({ data, error } = await run(orBuscaNomeCpf(t), 'nome, cpf, registrado_em'));        // sem colunas de empresa (migracao pendente)
  if (error) ({ data, error } = await run(orBuscaNomeCpfLegado(t), 'nome, cpf, registrado_em'));
  if (error) return [];
  const vistos = new Set(); const out = [];
  for (const r of (data || [])) {
    const chave = (r.cpf || '').replace(/\D/g, '') || (r.nome || '').toLowerCase();
    if (!chave || vistos.has(chave)) continue;
    vistos.add(chave); out.push(r);
    if (out.length >= 6) break;
  }
  return out;
}

async function listarHoje() {
  const base = (sel) => supabase.from('visita_administrativa').select(sel)
    .gte('registrado_em', inicioDoDiaISO()).order('registrado_em', { ascending: false });
  let resp = await base('id, nome, cpf, setor_nome, operador_nome, fala_com, fala_com_colaborador_id, empresa_representa, motivo_visita, detalhamento, registrado_em');
  if (resp.error) resp = await base('id, nome, cpf, setor_nome, operador_nome, fala_com, empresa_representa, motivo_visita, detalhamento, registrado_em');
  if (resp.error) resp = await base('id, nome, cpf, setor_nome, operador_nome, fala_com, empresa_representa, motivo_visita, registrado_em');
  if (resp.error) resp = await base('id, nome, cpf, setor_nome, registrado_em');
  if (resp.error) { console.error('[visita listarHoje]', resp.error.message); return []; }
  return resp.data || [];
}

export const paginaVisitaAdministrativa = {
  async render(container) {
    container.innerHTML = '';
    container.append(
      el('h1', { class: 'view__titulo', text: 'Visita administrativa' }),
      el('p', { class: 'view__sub', text: 'Visitante que não faz parte do quadro, vindo resolver uma pendência. Clique em "Registrar visita" e informe nome, CPF e o setor. Não há baixa de saída. A lista mostra as visitas de hoje (até virar o dia).' }),
    );

    const btn = el('button', { class: 'btn btn--primario', onClick: abrirRegistro },
      [el('i', { class: 'ph ph-user-square' }), ' Registrar visita']);
    const listaBox = el('div');
    const visBox = el('div', { style: 'display:none' });
    const btnHoje = el('button', { class: 'aba aba--ativa', onClick: () => trocarAbaVis('hoje') }, 'Visitas de hoje');
    const btnVis = el('button', { class: 'aba', onClick: () => trocarAbaVis('vis') }, 'Visitantes');
    container.append(
      el('div', { style: 'margin-bottom:14px' }, [btn]),
      el('div', { class: 'abas' }, [btnHoje, btnVis]),
      listaBox, visBox,
    );
    let visFeito = false;
    function trocarAbaVis(a) {
      btnHoje.classList.toggle('aba--ativa', a === 'hoje');
      btnVis.classList.toggle('aba--ativa', a === 'vis');
      listaBox.style.display = a === 'hoje' ? '' : 'none';
      visBox.style.display = a === 'vis' ? '' : 'none';
      if (a === 'vis' && !visFeito) { visFeito = true; desenharVisitantes(); }
    }

    const setores = await opcoesSetores();
    let motivos = await listarMotivos();
    let empresas = await listarEmpresas();

    function abrirRegistro() {
      const nome = el('input', { type: 'text', placeholder: 'Nome completo do visitante' });
      const cpf = el('input', { type: 'text', inputmode: 'numeric', maxlength: '14', placeholder: 'CPF' });
      cpf.addEventListener('input', () => { cpf.value = aplicarMascaraCpf(cpf.value); });
      const falaCom = el('input', { type: 'text', placeholder: 'Nome do servidor (pode digitar livre se não achar)' });
      const detalhe = el('input', { type: 'text', placeholder: 'Detalhe livre (ex.: Workshop de Cuidados Paliativos; com quem; etc.)' });
      let falaComColaboradorId = null;
      const sugVis = el('div', { class: 'mov-busca-sug', style: 'display:none; position:static; margin-top:4px' });
      const sugVisCpf = el('div', { class: 'mov-busca-sug', style: 'display:none; position:static; margin-top:4px' });
      const sugFala = el('div', { class: 'mov-busca-sug', style: 'display:none; position:static; margin-top:4px' });
      let sugTimerVis = null;
      function montarSugVis(vis, box) {
        clear(box);
        if (!vis.length) { box.style.display = 'none'; return; }
        box.append(el('div', { class: 'mov-busca-sug__tit', text: 'Visitantes que já vieram (clique para reaproveitar):' }));
        vis.forEach((vi) => box.append(el('div', { class: 'mov-busca-sug__item', onMousedown: (e) => {
          e.preventDefault(); nome.value = formatarNome(vi.nome); cpf.value = formatarCpf(vi.cpf || ''); sugVis.style.display = 'none'; sugVisCpf.style.display = 'none';
          if (vi.empresa_id != null) pickerEmpresa.escolher(vi.empresa_id, vi.empresa_representa); // reaproveita a empresa/orgao do ultimo registro (injeta se foi inativada)
        } }, [el('i', { class: 'ph ph-clock-user' }), el('span', { text: formatarNome(vi.nome) + ' - ' + mascararCpf(vi.cpf) + (vi.empresa_representa ? ' · ' + vi.empresa_representa : '') })])));
        box.style.display = 'block';
      }
      nome.addEventListener('input', () => {
        clearTimeout(sugTimerVis); const t = nome.value.trim();
        if (t.length < 2) { sugVis.style.display = 'none'; return; }
        sugTimerVis = setTimeout(async () => { const vis = await buscarVisitantesRecorrentes(t); if (nome.value.trim() === t) montarSugVis(vis, sugVis); }, 250);
      });
      nome.addEventListener('blur', () => setTimeout(() => { sugVis.style.display = 'none'; }, 150));
      let sugTimerCpf = null;
      cpf.addEventListener('input', () => {
        clearTimeout(sugTimerCpf); const t = cpf.value.trim();
        if (t.replace(/\D/g, '').length < 2) { sugVisCpf.style.display = 'none'; return; }
        sugTimerCpf = setTimeout(async () => { const vis = await buscarVisitantesRecorrentes(t); if (cpf.value.trim() === t) montarSugVis(vis, sugVisCpf); }, 250);
      });
      cpf.addEventListener('blur', () => setTimeout(() => { sugVisCpf.style.display = 'none'; }, 150));

      // "Com quem vai falar": sugere servidores cadastrados; aceita texto livre.
      let sugTimerFala = null;
      function montarSugFala(cols) {
        clear(sugFala);
        if (!cols.length) { sugFala.style.display = 'none'; return; }
        sugFala.append(el('div', { class: 'mov-busca-sug__tit', text: 'Servidores cadastrados (clique para usar):' }));
        cols.forEach((c) => sugFala.append(el('div', { class: 'mov-busca-sug__item', onMousedown: (e) => {
          e.preventDefault(); falaCom.value = formatarNome(c.nome); falaComColaboradorId = c.id || null; sugFala.style.display = 'none';
        } }, [el('i', { class: 'ph ph-user' }), el('span', { text: formatarNome(c.nome) })])));
        sugFala.style.display = 'block';
      }
      falaCom.addEventListener('input', () => {
        falaComColaboradorId = null;
        clearTimeout(sugTimerFala); const t = falaCom.value.trim();
        if (t.length < 2) { sugFala.style.display = 'none'; return; }
        sugTimerFala = setTimeout(async () => { const cols = await buscarColaboradoresFala(t); if (falaCom.value.trim() === t) montarSugFala(cols); }, 250);
      });
      falaCom.addEventListener('blur', () => setTimeout(() => { sugFala.style.display = 'none'; }, 150));

      // Combobox "criavel": tolera erro de digitacao (sugere o parecido) e, se nao casar, entende como NOVO.
      const picker = buscaSelect({ opcoes: setores, permitirNovo: true, criarRotulo: (t, tem) => tem ? `Nenhum destes? Cadastrar setor “${t}” (sugerir ao gestor)` : `Cadastrar setor “${t}” (sugerir ao gestor)` });
      const pickerMotivo = buscaSelect({ opcoes: motivos });
      const pickerEmpresa = buscaSelect({ opcoes: empresas, permitirNovo: true });

      abrirModal({
        titulo: 'Registrar visita administrativa', classe: 'modal--larga',
        corpo: [
          el('div', { class: 'campos-grid' }, [
            el('div', { class: 'campo' }, [el('label', { text: 'Nome completo *' }), limpavel(nome), sugVis]),
            el('div', { class: 'campo' }, [el('label', { text: 'CPF *' }), limpavel(cpf), sugVisCpf]),
            el('div', { class: 'campo campo--cheio' }, [el('label', { text: 'Setor a visitar *' }), picker.elemento]),
            el('div', { class: 'campo campo--cheio' }, [el('label', {}, [el('span', { text: 'Motivo da visita ' }), el('i', { class: 'ph ph-question', style: 'cursor:help; color:var(--muted)', title: 'O que entra em cada motivo (clique para ver)', onClick: abrirGuiaMotivo })]), pickerMotivo.elemento]),
            el('div', { class: 'campo' }, [el('label', { text: 'Vai falar com quem?' }), limpavel(falaCom), sugFala]),
            el('div', { class: 'campo' }, [el('label', { text: 'Representa empresa/órgão? (opcional)' }), pickerEmpresa.elemento]),
            el('div', { class: 'campo campo--cheio' }, [el('label', { text: 'Detalhamento (opcional)' }), limpavel(detalhe)]),
          ]),
        ],
        acoes: [
          { rotulo: 'Cancelar', classe: 'btn' },
          { rotulo: 'Registrar', classe: 'btn btn--primario', onClick: async () => {
            const nomeV = (nome.value || '').trim();
            if (!nomeV) { toast('Informe o nome completo.', 'erro'); return false; }
            if (!cpfValido(cpf.value)) { toast('CPF inválido.', 'erro'); return false; }

            // Setor (obrigatorio): escolhido da lista ou novo (sugestao ao gestor).
            const escSetor = picker.getEscolha();
            if (!escSetor) { toast('Informe o setor a visitar.', 'erro'); return false; }
            if (escSetor.tipo === 'ambiguo') { toast('Esse setor já existe — escolha-o na lista (ou clique em "Cadastrar" para sugerir um novo).', 'erro'); return false; }
            let setorId = null, setorNome = null; const setorNovo = escSetor.tipo === 'novo';
            if (setorNovo) { setorNome = escSetor.texto; }
            else { setorId = escSetor.value; setorNome = (setores.find((s) => String(s.value) === String(escSetor.value)) || {}).label || escSetor.label; }

            // Motivo (opcional): lista ou novo (pendente p/ aprovacao).
            const escMotivo = pickerMotivo.getEscolha();
            let motivoId = null, motivoNome = null;
            if (escMotivo && escMotivo.tipo === 'existente') { motivoId = escMotivo.value; motivoNome = (motivos.find((o) => String(o.value) === String(escMotivo.value)) || {}).nome || escMotivo.label; }
            // Empresa/orgao (opcional): lista ou novo (pendente p/ aprovacao).
            const escEmp = pickerEmpresa.getEscolha();
            if (escEmp && escEmp.tipo === 'ambiguo') { toast('Essa empresa/órgão já existe — escolha-a na lista (ou apague o texto para deixar em branco).', 'erro'); return false; }
            let empresaId = null, empresaNome = null; const empresaNovo = !!escEmp && escEmp.tipo === 'novo';
            if (escEmp) {
              if (escEmp.tipo === 'existente') { empresaId = escEmp.value; empresaNome = (empresas.find((o) => String(o.value) === String(escEmp.value)) || {}).nome || escEmp.label; }
              else { const e2 = await criarEmpresaPendente(escEmp.texto); if (e2) { empresaId = e2.id; empresaNome = e2.nome; } else { empresaNome = formatarTitulo(escEmp.texto); } }
            }

            const baseVis = { nome: nomeV, cpf: formatarCpf(cpf.value), setor_id: setorId, setor_nome: setorNome ? formatarTitulo(setorNome) : setorNome,
              operador_usuario_id: operadorAtual.id || null, operador_nome: operadorAtual.nome || null };
            const textos = {};
            if ((falaCom.value || '').trim()) textos.fala_com = formatarTitulo(falaCom.value);
            if (motivoNome) textos.motivo_visita = motivoNome;
            if (empresaNome) textos.empresa_representa = empresaNome;
            const extra = { ...textos };
            if ((detalhe.value || '').trim()) extra.detalhamento = detalhe.value.trim();
            if (motivoId) extra.motivo_id = motivoId;
            if (empresaId) extra.empresa_id = empresaId;
            if (falaComColaboradorId) extra.fala_com_colaborador_id = falaComColaboradorId;

            const ins = (obj) => supabase.from('visita_administrativa').insert(obj);
            const colFalta = (e) => e && (/column|schema cache/i.test(e.message || '') || e.code === 'PGRST204');
            let { error } = await ins({ ...baseVis, ...extra });
            if (colFalta(error)) { delete extra.fala_com_colaborador_id; ({ error } = await ins({ ...baseVis, ...extra })); } // 0058 nao rodada
            if (colFalta(error)) { delete extra.detalhamento; ({ error } = await ins({ ...baseVis, ...extra })); }            // 0057 nao rodada
            if (colFalta(error)) { delete extra.motivo_id; delete extra.empresa_id; ({ error } = await ins({ ...baseVis, ...extra })); } // 0054 nao rodada
            if (colFalta(error)) { ({ error } = await ins(baseVis)); }                                                       // 0039 nao rodada
            // Item escolhido foi fundido/descartado/excluido pelo gestor DEPOIS que esta tela carregou: a FK recusa o id.
            // Atualiza a lista NO LUGAR (o campo usa o mesmo array), limpa so o campo afetado e pede para escolher de novo,
            // sem perder o resto do que foi digitado. (setores e const e nunca era recarregado.)
            if (error && (error.code === '23503' || /foreign key/i.test(error.message || ''))) {
              const msgFk = error.message || '';
              const atualizar = async (arr, fonte) => { const novas = await fonte(); arr.splice(0, arr.length, ...novas); };
              if (/empresa_id/i.test(msgFk)) { await atualizar(empresas, listarEmpresas); pickerEmpresa.limpar(); toast('A empresa/órgão escolhida acabou de ser tirada da lista ou juntada pelo gestor. A lista foi atualizada: escolha de novo ou deixe em branco.', 'erro'); return false; }
              if (/motivo_id/i.test(msgFk)) { await atualizar(motivos, listarMotivos); pickerMotivo.limpar(); toast('O motivo escolhido acabou de ser alterado pelo gestor. A lista foi atualizada: escolha de novo.', 'erro'); return false; }
              if (/setor_id/i.test(msgFk)) { await atualizar(setores, opcoesSetores); picker.limpar(); toast('O setor escolhido acabou de ser juntado pelo gestor. A lista foi atualizada: escolha de novo.', 'erro'); return false; }
            }            if (error) { toast('Erro ao registrar: ' + error.message, 'erro'); return false; }

            if (setorNovo && setorNome) { sugerirSetor(setorNome, 'Informado ao registrar visita administrativa'); }
            const pend = [];
            if (setorNovo && setorNome) pend.push('setor');
            if (empresaNovo && empresaNome) pend.push('empresa');
            toast(pend.length ? `Visita registrada. Novo(s) ${pend.join(', ')} enviado(s) para aprovação do gestor.` : 'Visita registrada.', 'sucesso');
            motivos = await listarMotivos(); empresas = await listarEmpresas();
            desenhar();
            return true;
          } },
        ],
      });
    }

    async function desenhar(primeira = false) {
      if (primeira) { clear(listaBox); listaBox.append(loader()); }
      const linhas = await listarHoje();
      clear(listaBox);
      if (!linhas.length) { listaBox.append(el('p', { class: 'view__sub', text: 'Nenhuma visita administrativa registrada hoje.' })); return; }
      renderTabela(listaBox, {
        colunas: [
          { chave: 'nome', rotulo: 'Visitante', truncar: true, dica: 'Nome do visitante externo.', render: (l) => formatarNome(l.nome) },
          { chave: 'cpf', rotulo: 'CPF', largura: '130px', alinhar: 'centro', ordenavel: false, dica: 'CPF parcial (privacidade).', render: (l) => mascararCpf(l.cpf) },
          { chave: 'setor_nome', rotulo: 'Setor', truncar: true, dica: 'Setor que o visitante procurou.' },
          { chave: 'fala_com', rotulo: 'Falar com', truncar: true, ordenavel: false, dica: 'Servidor/contato que o visitante procura. O visto verde indica vinculo com servidor cadastrado.', render: (l) => l.fala_com ? (l.fala_com_colaborador_id ? el('span', {}, [el('i', { class: 'ph ph-user-check', style: 'color:var(--verde); margin-right:5px', title: 'Vinculado a um servidor cadastrado' }), l.fala_com]) : l.fala_com) : '' },
          { chave: 'motivo_visita', rotulo: 'Motivo', truncar: true, ordenavel: false, dica: 'Categoria do motivo da visita.', render: (l) => l.motivo_visita || '' },
          { chave: 'detalhamento', rotulo: 'Detalhe', truncar: true, ordenavel: false, dica: 'Detalhamento livre informado.', render: (l) => l.detalhamento || '' },
          { chave: 'registrado_em', rotulo: 'Hora', largura: '150px', dica: 'Quando a visita foi registrada.', render: (l) => fmtDataHora(l.registrado_em) },
          { chave: 'operador_nome', rotulo: 'Registrado por', largura: '160px', truncar: true, ordenavel: false, dica: 'Recepcionista que registrou.', render: (l) => formatarNome(l.operador_nome || '') },
          { chave: '_acao', rotulo: '', largura: '70px', alinhar: 'direita', ordenavel: false, render: (l) => ehGestaoAtivo() ? el('button', { class: 'btn', title: 'Editar visitante', 'aria-label': 'Editar visitante', onClick: () => abrirEditarVisitante({ nome: l.nome, cpf: l.cpf }) }, [el('i', { class: 'ph ph-pencil-simple' })]) : '' },
        ],
        linhas, buscarEm: ['nome', 'cpf', 'setor_nome', 'operador_nome', 'fala_com', 'motivo_visita', 'detalhamento'], porPagina: 12,
      });
    }

    async function desenharVisitantes() {
      clear(visBox);
      visBox.append(el('p', { class: 'view__sub', text: 'Visitantes que já vieram (agrupados por CPF). Editar corrige nome/CPF em TODAS as visitas da pessoa.' }));
      const box = el('div'); visBox.append(box);
      box.append(loader());
      const { data, error } = await supabase.rpc('visitantes_registro');
      clear(box);
      if (error || !data || !data.length) { box.append(el('p', { class: 'view__sub', text: 'Nenhum visitante registrado ainda.' })); return; }
      const colunas = [
        { chave: 'nome', rotulo: 'Visitante', truncar: true, dica: 'Nome do visitante.', render: (l) => formatarNome(l.nome) },
        { chave: 'cpf', rotulo: 'CPF', largura: '130px', alinhar: 'centro', ordenavel: false, dica: 'CPF parcial (privacidade).', render: (l) => mascararCpf(l.cpf) },
        { chave: 'visitas', rotulo: 'Visitas', largura: '90px', alinhar: 'centro', dica: 'Quantas vezes veio.' },
        { chave: 'ultima', rotulo: 'Última visita', largura: '150px', dica: 'Data da visita mais recente.', render: (l) => fmtDataHora(l.ultima) },
        { chave: 'ultimo_setor', rotulo: 'Último setor', truncar: true, dica: 'Setor procurado na última visita.' },
      ];
      if (ehGestaoAtivo()) colunas.push({ chave: '_acao', rotulo: '', largura: '70px', alinhar: 'direita', ordenavel: false, render: (l) => el('button', { class: 'btn', title: 'Editar visitante', 'aria-label': 'Editar visitante', onClick: () => abrirEditarVisitante(l) }, [el('i', { class: 'ph ph-pencil-simple' })]) });
      renderTabela(box, { colunas, linhas: data, buscarEm: ['nome', 'cpf', 'ultimo_setor'], porPagina: 12 });
    }
    function abrirEditarVisitante(vis) {
      const cpfAntigo = vis.cpf;
      const nomeI = el('input', { type: 'text', value: formatarNome(vis.nome) });
      const cpfI = el('input', { type: 'text', inputmode: 'numeric', maxlength: '14', value: vis.cpf || '' });
      cpfI.addEventListener('input', () => { cpfI.value = aplicarMascaraCpf(cpfI.value); });
      abrirModal({
        titulo: 'Editar visitante',
        corpo: [
          el('p', { class: 'view__sub', style: 'margin-top:0', text: 'A correção vale para TODAS as visitas desta pessoa' + (vis.visitas ? ' (' + vis.visitas + ' registro(s))' : '') + '.' }),
          el('div', { class: 'campo' }, [el('label', { text: 'Nome' }), limpavel(nomeI)]),
          el('div', { class: 'campo' }, [el('label', { text: 'CPF' }), limpavel(cpfI)]),
        ],
        acoes: [
          { rotulo: 'Cancelar', classe: 'btn' },
          { rotulo: 'Salvar em todas', classe: 'btn btn--primario', onClick: async () => {
            const nomeNovo = (nomeI.value || '').trim();
            if (!nomeNovo) { toast('Informe o nome.', 'erro'); return false; }
            if (!cpfValido(cpfI.value)) { toast('CPF inválido.', 'erro'); return false; }
            const r = await supabase.rpc('editar_visitante', { p_cpf_antigo: cpfAntigo, p_nome: nomeNovo, p_cpf_novo: formatarCpf(cpfI.value) });
            if (r.error) { toast('Erro: ' + r.error.message, 'erro'); return false; }
            toast('Visitante atualizado em ' + (r.data || 0) + ' registro(s).', 'sucesso');
            desenharVisitantes(); desenhar();
            return true;
          } },
        ],
      });
    }

    desenhar(true);
    assinarTabela('visita_administrativa', desenhar);
    pollPeriodico(desenhar);
  },
};